import React, { useState, useMemo } from "react";
import { Box, Typography, Tabs, Tab } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

import CollapseCard from "components/Collapse";
import TwoColumnContainer from "components/Container/TwoColumnContainer";

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.customFontSize[2],
  color: theme.palette.primary.black,
  fontWeight: theme.typography.customFontWeight[1],
}));

const fields = [
  { key: "type", label: "WAN Type" },
  { key: "ipv4_ipAddress", label: "WAN IPv4" },
  { key: "ipv4_gateway", label: "IPv4 Gateway" },
  { key: "ipv4_dns1", label: "DNS Server 1" },
  { key: "ipv4_dns2", label: "DNS Server 2" },
  { key: "ipv6_ipAddress", label: "WAN IPv6" },
  { key: "ipv6_gateway", label: "IPv6 Gateway" },
  { key: "ipv6_dns1", label: "IPv6 DNS Server 1" },
  { key: "ipv6_dns2", label: "IPv6 DNS Server 2" },
];

const InternetPanel = ({ routerInternetData }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);

  const wanList = useMemo(() => {
    if (Array.isArray(routerInternetData)) {
      return routerInternetData;
    }

    if (routerInternetData && (routerInternetData.ipv4 || routerInternetData.ipv6)) {
      return [routerInternetData];
    }

    return [];
  }, [routerInternetData]);

  const currentData = useMemo(() => {
    const selectedWan = wanList[tabValue] || {};

    return Object.entries(selectedWan).reduce((acc, [key, item]) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        const { type, ...rest } = item;

        Object.entries(rest).forEach(([k, v]) => {
          acc[`${key}_${k}`] = v;
        });

        // Map the type to a more user-friendly format if it exists
        if (type) {
          const typeMapping = {
            auto: "Auto",
            dhcp: "DHCP",
            pppoe: "PPPoE",
          };
          acc.type = typeMapping[type.toLowerCase()] || type;
        }
      }

      return acc;
    }, {});
  }, [wanList, tabValue]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (wanList.length === 0) return null;

  return (
    <CollapseCard title="Internet">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            border: `1px solid ${theme.palette.primary.greyThread}`,
            borderRadius: "100px",
            backgroundColor: theme.palette.primary.white,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              minHeight: 38,
              "& .MuiTab-root": {
                textTransform: "none",
                minHeight: 38,
                borderRadius: "100px",
                color: theme.palette.primary.black,
                transition: "all 0.2s ease",

                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.subBlue,
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            {wanList.map((wan, index) => (
              <Tab key={index} label={wan.name || `WAN ${index + 1}`} />
            ))}
          </Tabs>
        </Box>

        {fields.map(({ key, label }) => (
          <TwoColumnContainer key={key} label={label}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                alignItems: "center",
              }}
            >
              <StyledTypography>
                {currentData[key] ?? "-"}
              </StyledTypography>
            </Box>
          </TwoColumnContainer>
        ))}
      </Box>
    </CollapseCard>
  );
};

export default InternetPanel;
